import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  Button,
  Input,
  Table,
  Form,
  Label,
  Row,
  Col
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  addClientPreQuestion,
  getClientPreQuestionForEdit,
  getJobByClient,
  resetClientPreQuestionMasterSort,
  updateClientPreQuestion,
} from "model/ClientPreQuestionModel";

const sortDetailsBySortField = (details = []) =>
  [...(details || [])].sort((a, b) => {
    const sortA = Number(a?.sort ?? a?.sortOrder ?? 0);
    const sortB = Number(b?.sort ?? b?.sortOrder ?? 0);
    return sortA - sortB;
  });

const createEmptyRow = () => ({
  questionTypeId: null,
  question: "",
  expectedAnswer: "",
  forFunnelJob: false,
  isMandatory: false,
  sortOrder: 0,
  sort: 0,
});

const PreQuestioningAddEditModalData = ({
  preQuestioningAddEditModalOpen,
  setPreQuestioningAddEditModalOpen,
  itemId,
  getQuestionTypeList,
  formData,
  setFormData,
  questionTypeDetails
}) => {
  const [jobOptions, setJobOptions] = useState([]);
  const isEditMode = Boolean(itemId);

  const closeModal = () => {
    setPreQuestioningAddEditModalOpen(false);
    setJobOptions([]);
    setFormData(prev => ({ ...prev, questions: [] }));
  };

  const handleRootChange = (field, value) => {
    setFormData((prev) => {
      if (field === "clientId") {
        return {
          ...prev,
          clientId: value,
          jobId: "",
          questions: [],
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  /* ---------------- Add Row ---------------- */
  const handleAddRow = () => {
    if (!isEditMode && !formData?.jobId) {
      toast.error("Please select job first");
      return;
    }
    setFormData(prev => ({
      ...prev,
      questions: [
        ...(prev.questions || []),
        { ...createEmptyRow(), sortOrder: (prev.questions || []).length, sort: (prev.questions || []).length },
      ],
    }));
  };

  const handleDeleteRow = (index) => {
    setFormData(prev => {
      const nextQuestions = (prev.questions || [])
        .filter((_, i) => i !== index)
        .map((row, idx) => ({
          ...row,
          sortOrder: idx,
          sort: idx,
        }));
      return {
        ...prev,
        questions: nextQuestions,
      };
    });
  };

  const handleQuestionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const mapDetailsToQuestions = (details = []) =>
    sortDetailsBySortField(details).map((questionItem, index) => ({
      questionTypeId: questionItem?.questionTypeId || questionItem?.id || null,
      question: questionItem?.questionText || questionItem?.name || "",
      expectedAnswer: questionItem?.expectedAnswer ?? "",
      forFunnelJob: !!(questionItem?.forFunnelJob ?? questionItem?.forFunnlJob),
      isMandatory: !!(questionItem?.isMandatory ?? questionItem?.isMendatory),
      sortOrder: Number(questionItem?.sortOrder ?? questionItem?.sort ?? index ?? 0),
      sort: Number(questionItem?.sort ?? questionItem?.sortOrder ?? index ?? 0),
    }));

  const handleDragEnd = (result) => {
    if (!result?.destination) return;
    if (result.destination.index === result.source.index) return;

    setFormData((prev) => {
      const currentQuestions = [...(prev?.questions || [])];
      const [movedRow] = currentQuestions.splice(result.source.index, 1);
      currentQuestions.splice(result.destination.index, 0, movedRow);
      const reorderedQuestions = currentQuestions.map((row, idx) => ({
        ...row,
        sortOrder: idx,
        sort: idx,
      }));
      return {
        ...prev,
        questions: reorderedQuestions,
      };
    });
  };

  const fetchJobsByClientId = async (targetClientId) => {
    if (!targetClientId) {
      setJobOptions([]);
      return;
    }
    const response = await getJobByClient(targetClientId);
    const records = response?.data?.records || response?.data || [];
    const options = (Array.isArray(records) ? records : [])
      .map((item) => ({
        value: item?.jobId || item?.id || item?._id || "",
        label: item?.job || item?.display || item?.name || item?.title || "Job",
      }))
      .filter((item) => item?.value);
    setJobOptions(options);
  };

  const fetchQuestionDetailsByClientAndJob = async (targetClientId, targetJobId) => {
    if (!targetClientId || !targetJobId) return;
    const response = await getClientPreQuestionForEdit(targetClientId, targetJobId);
    const responseData = response?.data || {};
    const editRow = responseData?.record || responseData?.data || responseData;
    const details = editRow?.details || responseData?.details || [];
    if (details?.length) {
      setFormData((prev) => ({
        ...prev,
        id: editRow?.id || prev?.id || "",
        clientId: editRow?.clientId || targetClientId,
        jobId: editRow?.jobId || targetJobId,
        questions: mapDetailsToQuestions(details),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        clientId: targetClientId,
        jobId: targetJobId,
        questions: [],
      }));
    }
  };

  const handleJobSelectChange = async (option) => {
    const selectedJobId = option?.value || "";
    setFormData((prev) => ({
      ...prev,
      jobId: selectedJobId,
      questions: [],
    }));
    if (selectedJobId && formData?.clientId) {
      await fetchQuestionDetailsByClientAndJob(formData.clientId, selectedJobId);
    }
  };

  const selectedJobOption =
    jobOptions.find((opt) => String(opt?.value) === String(formData?.jobId || "")) || null;


  const handleResetMasterSort = async () => {
    const preQuestionId = String(formData?.id || itemId || "").trim();
    if (!preQuestionId) {
      toast.error("Pre question ID is required");
      return;
    }

    const swalResult = await Swal.fire({
      title: "Reset master sorting?",
      text: "Are you sure you want to reset master sorting for this pre question?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      target: document.body,
      allowOutsideClick: false,
      didOpen: () => {
        const el = document.querySelector(".swal2-container");
        if (el) el.style.zIndex = 20000;
      },
    });
    if (!swalResult?.isConfirmed) return;

    const response = await resetClientPreQuestionMasterSort(preQuestionId);
    if (response?.isSuccess || response?.success) {
      toast.success(response?.message || "Master sorting reset successfully");
      closeModal();
      getQuestionTypeList?.();
      return;
    }
    toast.error(response?.message || "Unable to reset master sorting");
  };

  const handleSubmit = async () => {
    if (!formData?.clientId) {
      toast.error("Client is required");
      return;
    }

    if (!formData?.jobId) {
      toast.error("Job is required");
      return;
    }

    if (!formData?.questions?.length) {
      toast.error("Please add at least one question");
      return;
    }

    if (formData?.questions?.some(q => !q.question.trim())) {
      toast.error("Question field is required");
      return;
    }

    const params = {
      id: formData?.id || questionTypeDetails?.id || itemId || "",
      clientId: formData?.clientId || questionTypeDetails?.clientId || "",
      jobId: formData?.jobId || questionTypeDetails?.jobId || "",
      details: formData?.questions?.map((innerQuestionItem, index) => ({
        questionTypeId: innerQuestionItem?.questionTypeId || innerQuestionItem?.id || null,
        questionText: innerQuestionItem?.question || "",
        expectedAnswer: innerQuestionItem?.expectedAnswer || "",
        forFunnelJob: !!(innerQuestionItem?.forFunnelJob ?? innerQuestionItem?.forFunnlJob),
        isMandatory: !!(innerQuestionItem?.isMandatory ?? innerQuestionItem?.isMendatory),
        sortOrder: Number(innerQuestionItem?.sortOrder ?? innerQuestionItem?.sort ?? index ?? 0),
      }))
    };

    const response = itemId
      ? await updateClientPreQuestion(itemId, params)
      : await addClientPreQuestion(params);
    if (response?.isSuccess) {
      toast.success(response?.message);
      closeModal();
      getQuestionTypeList?.();
    } else {
      toast.error(response?.message);
    }
  };


  /* ---------------- Edit Mode ---------------- */
  useEffect(() => {
    if (itemId && questionTypeDetails) {
      setFormData({
        id: questionTypeDetails?.id || itemId || "",
        clientId: questionTypeDetails?.clientId || "",
        jobId: questionTypeDetails?.jobId || "",
        questions: mapDetailsToQuestions(questionTypeDetails?.details || []),
      });
    }
  }, [itemId, questionTypeDetails]);

  useEffect(() => {
    if (!preQuestioningAddEditModalOpen || isEditMode) return;
    if (formData?.clientId) {
      fetchJobsByClientId(formData.clientId);
    }
  }, [preQuestioningAddEditModalOpen, isEditMode, formData?.clientId]);

  useEffect(() => {
    if (!preQuestioningAddEditModalOpen || isEditMode) return;
    if (formData?.clientId && formData?.jobId) {
      fetchQuestionDetailsByClientAndJob(formData.clientId, formData.jobId);
    }
  }, [preQuestioningAddEditModalOpen, isEditMode, formData?.clientId, formData?.jobId]);


  return (
    <Modal
      isOpen={preQuestioningAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      size="xl"
      centered
      zIndex={5000}
      className="modal-full-height question-type-modal"
    >
      <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0">
              {itemId ? "Edit" : "Create"} Pre Questioning
            </h5>
            <Button close onClick={closeModal} />
          </div>

          <hr />
          {!isEditMode ? (
            <Row className="justify-content-center align-items-center">
              {/* <Col md={6}>
                <Label>Client ID <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  name="clientId"
                  value={formData?.clientId || ""}
                  onChange={e =>
                    handleRootChange("clientId", e.target.value)
                  }
                  onBlur={(e) => fetchJobsByClientId(e?.target?.value)}
                  placeholder="Client ID"
                />
              </Col> */}
              <Col md={8} lg={7}>
                <Label>Job <span className="text-danger">*</span></Label>
                <Select
                  options={jobOptions}
                  className="select2-selection"
                  placeholder="Select Job"
                  value={selectedJobOption}
                  onChange={handleJobSelectChange}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Col>
            </Row>
          ) : null}
          <div className="text-end mt-3 d-flex justify-content-end gap-2">
            {formData?.id || itemId ? (
              <Button
                color="success"
                type="button"
                onClick={handleResetMasterSort}
              >
                Reset Master Sorting
              </Button>
            ) : null}
            <Button
              color="primary"
              type="button"
              onClick={handleAddRow}
            >
              + Add
            </Button>
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Table responsive bordered className="mt-3">
              <thead>
                <tr>
                  <th style={{ width: "70px" }}>#</th>
                  <th>Question <span className="text-danger">*</span> </th>
                  <th>Expected Answer</th>
                  <th className="text-center col-mandatory">For Funnel Job</th>
                  <th className="text-center col-mandatory">Is Mandatory</th>
                  <th className="text-center col-actions">Actions</th>
                </tr>
              </thead>
              <Droppable droppableId="pre-question-rows" type="QUESTION">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {formData?.questions?.map((row, index) => (
                      <Draggable
                        key={`pre-question-${row?.questionTypeId || "new"}-${index}`}
                        draggableId={`pre-question-${row?.questionTypeId || "new"}-${index}`}
                        index={index}
                      >
                        {(providedDraggable) => (
                          <tr
                            ref={providedDraggable.innerRef}
                            {...providedDraggable.draggableProps}
                            key={`pre-question-row-${index}`}
                          >
                            <td className="align-middle">
                              <div className="d-flex align-items-center gap-2">
                                <span {...providedDraggable.dragHandleProps}>
                                  <i className="fa fa-grip-vertical cursor-pointer"></i>
                                </span>
                                <span>{index + 1}</span>
                              </div>
                            </td>

                            <td>
                              <Input
                                type="textarea"
                                value={row.question}
                                onChange={e =>
                                  handleQuestionChange(index, "question", e.target.value)
                                }
                                placeholder="Enter Question"
                                rows="2"
                              />
                            </td>

                            <td>
                              <Input
                                type="textarea"
                                value={row.expectedAnswer}
                                onChange={e =>
                                  handleQuestionChange(
                                    index,
                                    "expectedAnswer",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Expected Answer"
                                rows="2"
                              />
                            </td>
                            <td className="col-mandatory text-center vertical-allign-middle">
                              <Input
                                key={`forFunnelJob-${index}-${row?.questionTypeId || ""}-${row?.forFunnelJob ? "1" : "0"}`}
                                type="checkbox"
                                defaultChecked={!!row.forFunnelJob}
                                onChange={e =>
                                  handleQuestionChange(
                                    index,
                                    "forFunnelJob",
                                    e.target.checked
                                  )
                                }
                              />
                            </td>
                            <td className="col-mandatory text-center vertical-allign-middle">
                              <Input
                                key={`isMandatory-${index}-${row?.questionTypeId || ""}-${row?.isMandatory ? "1" : "0"}`}
                                type="checkbox"
                                defaultChecked={!!row.isMandatory}
                                onChange={e =>
                                  handleQuestionChange(
                                    index,
                                    "isMandatory",
                                    e.target.checked
                                  )
                                }
                              />
                            </td>

                            <td className="col-actions text-center vertical-allign-middle">
                              <i className="mdi mdi-trash-can font-size-18 text-danger" onClick={() => handleDeleteRow(index)} />
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}

                    {!formData.questions?.length && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          No data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </Droppable>
            </Table>
          </DragDropContext>

          {/* Footer */}
          <div className="text-end mt-3">
            <Button color="primary" type="submit">
              {itemId ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default PreQuestioningAddEditModalData;
